import React, { useState } from "react";

import _ from "lodash";

import { useListLumenV2FormulaVersions } from "manager/hooks/api";
import { getLumenV2Formula } from "manager/lib/lumenApi";
import Icon from "shared/components/Icon";
import "./FormulaVersionHistory.css";

interface FormulaVersionHistoryProps {
  formula: any;
  onVersionSelect?: (version: any) => void;
  selectedVersionTimestamp?: number;
}

const FormulaVersionHistory: React.FC<FormulaVersionHistoryProps> = ({
  formula,
  onVersionSelect,
  selectedVersionTimestamp,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [loadingVersionId, setLoadingVersionId] = useState<string | null>(null);

  // Parse the formula ID to get company and identifier
  const formulaId = formula?.expression?.id || formula?.id;

  // Handle both VRN format (vrn:expression::company/identifier:version)
  // and simple format (company/identifier)
  let company = "";
  let identifier = "";
  let vrnFormulaId = "";

  if (formulaId) {
    // Check if it's a VRN format
    if (formulaId.includes("::")) {
      const parts = formulaId.split("::");
      if (parts.length >= 2) {
        const pathPart = parts[1];
        const colonIndex = pathPart.lastIndexOf(":");
        const pathWithoutVersion =
          colonIndex !== -1 ? pathPart.substring(0, colonIndex) : pathPart;
        const slashIndex = pathWithoutVersion.indexOf("/");
        if (slashIndex !== -1) {
          company = pathWithoutVersion.substring(0, slashIndex);
          identifier = pathWithoutVersion.substring(slashIndex + 1);
          // Strip 'lumen-' prefix if present
          if (identifier.startsWith("lumen-")) {
            identifier = identifier.substring(6);
          }
        }
      }
    } else {
      // Simple format - just split on slash
      const slashIndex = formulaId.indexOf("/");
      if (slashIndex !== -1) {
        company = formulaId.substring(0, slashIndex);
        identifier = formulaId.substring(slashIndex + 1);
        // Strip 'lumen-' prefix if present
        if (identifier.startsWith("lumen-")) {
          identifier = identifier.substring(6);
        }
      }
    }

    // Build the unprefixed formula ID for the API
    if (company && identifier) {
      vrnFormulaId = `${company}/${identifier}`;
    }
  }

  if (!company || !identifier) {
    return (
      <div className="formula-version-history error">
        Unable to load version history - invalid formula ID format
      </div>
    );
  }

  // Now fetch version history from the new endpoint
  const { data: versions, isLoading } = useListLumenV2FormulaVersions({
    formulaId: vrnFormulaId,
    options: {},
  });

  // Sort versions by timestamp in descending order
  const sortedVersions = _.orderBy(
    (versions as any[]) || [],
    ["timestamp"],
    ["desc"]
  );

  // The most recent version is the current one
  const currentVersionData = sortedVersions[0];
  const previousVersions = sortedVersions.slice(1);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const formatTimestamp = (timestamp: string | number) => {
    if (!timestamp) return "";
    const date = new Date(parseInt(timestamp.toString(), 10));
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    // Format the date
    const dateStr = date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    const timeStr = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    // Add relative time
    let relativeTime = "";
    if (diffMins < 1) {
      relativeTime = "just now";
    } else if (diffMins < 60) {
      relativeTime = `${diffMins} minute${diffMins !== 1 ? "s" : ""} ago`;
    } else if (diffHours < 24) {
      relativeTime = `about ${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
    } else {
      const diffDays = Math.floor(diffHours / 24);
      relativeTime = `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
    }

    return `${dateStr} ${timeStr}  (${relativeTime})`;
  };

  const handleVersionClick = async (version) => {
    setLoadingVersionId(version.id);
    try {
      // Fetch the detailed version data including expression
      const detailedVersion = await getLumenV2Formula(version.id, true);
      if (onVersionSelect && detailedVersion) {
        onVersionSelect(detailedVersion);
      }
    } catch (error) {
      console.error("Error fetching version details:", error);
    } finally {
      setLoadingVersionId(null);
    }
  };

  if (isLoading) {
    return (
      <div className="formula-version-history loading">
        <Icon.LoadingSpinnerGreenIcon /> Loading version history...
      </div>
    );
  }

  if (!versions || (versions as any[]).length === 0) {
    return null;
  }

  return (
    <div className="vega version-history">
      <div className="vega label">Version History</div>

      <div className="vega versions-list">
        {currentVersionData && (
          <button
            type="button"
            className={`vega version-item current ${
              !selectedVersionTimestamp ||
              selectedVersionTimestamp === currentVersionData.timestamp
                ? "selected"
                : ""
            }`}
            onClick={() => onVersionSelect && onVersionSelect(null)}
            disabled={!onVersionSelect}
          >
            <Icon.RefreshIcon className="vega icon" />
            <div className="vega version-info">
              <div className="vega version-label">
                Current Version
                {(!selectedVersionTimestamp ||
                  selectedVersionTimestamp ===
                    currentVersionData.timestamp) && (
                  <span className="vega viewing-badge">Viewing</span>
                )}
              </div>
              <div className="vega version-timestamp">
                {formatTimestamp(currentVersionData.timestamp)}
              </div>
            </div>
          </button>
        )}

        {previousVersions.length > 0 && (
          <>
            <button
              type="button"
              className="vega version-separator clickable"
              onClick={toggleExpanded}
            >
              <span>Previous Versions ({previousVersions.length})</span>
              <Icon.ChevronDownIcon
                className={`vega chevron ${isExpanded ? "expanded" : ""}`}
                style={{ marginLeft: "8px", transition: "transform 0.2s" }}
              />
            </button>
            {isExpanded && (
              <>
                {_.map(previousVersions, (version, index) => (
                  <button
                    key={version.timestamp}
                    type="button"
                    className={`vega version-item ${
                      selectedVersionTimestamp === version.timestamp
                        ? "selected"
                        : ""
                    }`}
                    onClick={() => handleVersionClick(version)}
                    disabled={loadingVersionId === version.id}
                  >
                    <Icon.HistoryIcon className="vega icon" />
                    <div className="vega version-info">
                      <div className="vega version-label">
                        Version {previousVersions.length - index}
                        {selectedVersionTimestamp === version.timestamp && (
                          <span className="vega viewing-badge">Viewing</span>
                        )}
                      </div>
                      <div className="vega version-timestamp">
                        {formatTimestamp(version.timestamp)}
                      </div>
                    </div>
                  </button>
                ))}
              </>
            )}
          </>
        )}

        {previousVersions.length === 0 && (
          <div className="vega no-previous-versions">
            No previous versions available
          </div>
        )}
      </div>
    </div>
  );
};

export default FormulaVersionHistory;
